/* =========================================================================
   MARKETING Hero — Facebook Interest Intelligence: shared types & constants.
   Shared by the AI mock, the API, and the frontend so the shape is identical
   whether the answer comes from Mock or real Claude.
   ========================================================================= */

/** The 18 audience concept layers from the spec. */
export const AUDIENCE_LAYERS: { id: string; label: string }[] = [
  { id: "direct", label: "Direct Interest — ความสนใจตรง" },
  { id: "product", label: "Product Interest — สนใจตัวสินค้า" },
  { id: "brand", label: "Brand Interest — แบรนด์ที่เกี่ยวข้อง" },
  { id: "competitor", label: "Competitor Ecosystem — ระบบนิเวศคู่แข่ง" },
  { id: "related", label: "Related Product — สินค้าใกล้เคียง" },
  { id: "lifestyle", label: "Lifestyle — ไลฟ์สไตล์" },
  { id: "profession", label: "Profession — อาชีพ" },
  { id: "business_owner", label: "Business Owner / Career Proxy — เจ้าของกิจการ" },
  { id: "purchasing_power", label: "Purchasing Power Proxy — กำลังซื้อ" },
  { id: "family", label: "Family / Life Stage — ครอบครัว/ช่วงชีวิต" },
  { id: "hobby", label: "Hobby — งานอดิเรก" },
  { id: "media", label: "Media / Content Consumption — สื่อที่เสพ" },
  { id: "location", label: "Location Logic — ตรรกะพื้นที่" },
  { id: "hidden", label: "Hidden Interest — ความสนใจซ่อนเร้น" },
  { id: "indirect", label: "Indirect Interest — ความสนใจทางอ้อม" },
  { id: "experimental", label: "Experimental — ทดลอง" },
  { id: "broad", label: "Broad Strategy — กว้าง" },
  { id: "retargeting", label: "Retargeting Strategy — รีทาร์เก็ต" },
];

/** Cluster archetypes from the spec. */
export const CLUSTER_TYPES: { id: string; label: string }[] = [
  { id: "direct", label: "Direct Cluster" },
  { id: "lifestyle", label: "Lifestyle Cluster" },
  { id: "profession", label: "Profession Cluster" },
  { id: "purchasing_power", label: "Purchasing Power Cluster" },
  { id: "hidden", label: "Hidden Cluster" },
  { id: "experimental", label: "Experimental Cluster" },
  { id: "broad", label: "Broad" },
];

export const META_DISCLAIMER =
  "⚠️ Targeting options ของ Meta เปลี่ยนแปลงได้ตลอด — คำเหล่านี้คือ “คำค้นที่คาดว่าใช้ได้ (Potential Search Term)” ไม่ใช่การยืนยันว่ามีอยู่จริงใน Ads Manager กรุณาตรวจสอบใน Meta Ads Manager ก่อนใช้งานจริงทุกครั้ง";

export interface InterestIdea {
  concept: string; // audience strategy concept — who this group is
  metaSearchTerm: string; // potential meta interest search term (NOT confirmed)
  layer: string; // one of AUDIENCE_LAYERS id
  clusterRef: string; // local cluster id (e.g. "c1")
  whyMatter: string; // plain-language: why this group is worth targeting
  hook: string; // a hook idea for this group
  confidence: "high" | "medium" | "low";
  testingPriority: number; // 1 (highest) .. 5
  risk: string;
}

export interface InterestCluster {
  ref: string; // local id e.g. "c1"
  name: string;
  type: string; // CLUSTER_TYPES id
  hypothesis: string;
  logic: string;
  creativeAngle: string;
  offerAngle: string;
  testObjective: string;
  budgetSuggestion: string;
  kpi: string;
  killSignal: string;
  scaleSignal: string;
  metaInterests?: string[]; // 8-12 keywords ready to STACK in one Meta ad set
}

export interface InterestOutput {
  persona: string;
  clusters: InterestCluster[];
  ideas: InterestIdea[];
  disclaimer: string;
}

export interface DiscoveryInputs {
  objective?: string;
  campaignType?: string;
  location?: string;
  customerType?: string;
  age?: string;
  gender?: string;
  profession?: string;
  lifestyle?: string;
  pain?: string;
  desire?: string;
  purchasingPower?: string;
  product?: string;
  price?: string;
  extra?: string;
}

/** JSON schema hint handed to the LLM so real Claude returns the same shape. */
export const INTEREST_SCHEMA_HINT = {
  persona: "string — สรุป persona สั้นๆ",
  clusters: [
    {
      ref: "c1",
      name: "string",
      type: "direct|lifestyle|profession|purchasing_power|hidden|experimental|broad",
      hypothesis: "string",
      logic: "string",
      creativeAngle: "string",
      offerAngle: "string",
      testObjective: "string",
      budgetSuggestion: "string เช่น ฿200-300/วัน",
      kpi: "string",
      killSignal: "string",
      scaleSignal: "string",
      metaInterests: ["คำ interest 8-12 คำ ที่เอาไปใส่ซ้อนกันใน ad set เดียวได้ (เช่น ชื่อแบรนด์/เพจ/หมวดที่ Meta มักมี)"],
    },
  ],
  ideas: [
    {
      concept: "string — กลุ่มนี้คือใคร (สั้น)",
      metaSearchTerm: "string — คำค้นที่คาดว่ามีใน Meta (ห้ามฟันธงว่ามีจริง)",
      layer: "direct|product|brand|competitor|related|lifestyle|profession|business_owner|purchasing_power|family|hobby|media|location|hidden|indirect|experimental|broad|retargeting",
      clusterRef: "c1",
      whyMatter: "string — ทำไมกลุ่มนี้น่ายิง (ภาษาบ้านๆ)",
      hook: "string — ไอเดีย hook สั้นๆ",
      confidence: "high|medium|low",
      testingPriority: 1,
      risk: "string",
    },
  ],
};
